import { useState, useCallback, useEffect, useRef, memo, useMemo } from 'react';
import { Av } from './shared';
import { useApp } from '../context/AppContext';
import { markAsRead, markAllRead, deleteNotification } from '../services/notifications';
import { getDashboardStats } from '../services/api';

/* ── NAV ITEMS with route paths ── */
export const NAV = [
  {path:"/dashboard",ico:"⬡",lbl:"Dashboard"},
  {path:"/ai",ico:"✦",lbl:"AI Intelligence",badge:"new"},
  {id:"team",ico:"👥",lbl:"Team",ch:[
    {path:"/team/users",lbl:"Users"},
    {path:"/team/subteams",lbl:"Subteams"},
    {path:"/team/users/new",lbl:"Create User"},
    {path:"/team/requests",lbl:"Requests"},
  ]},
  {id:"leads",ico:"🎯",lbl:"Leads",ch:[
    {path:"/leads",lbl:"All Leads"},
    {path:"/leads/map",lbl:"Maps"},
    {path:"/leads/board",lbl:"LeadsBoard"},
  ]},
  {path:"/leads/insights",ico:"📈",lbl:"Analytics & Reports"},
  {id:"campaigns",ico:"📡",lbl:"Campaign",ch:[
    {path:"/campaigns/email",lbl:"Email Campaign"},
    {path:"/campaigns/notification",lbl:"Notification"},
  ]},
  {path:"/automations",ico:"⚡",lbl:"Smart Automations"},
  {path:"/activity",ico:"📋",lbl:"Activity Feed"},
  {path:"/support",ico:"🛟",lbl:"Support"},
  {path:"/settings",ico:"⚙️",lbl:"Settings"},
];

/* ── Helper: check if a path is active ── */
const isActive = (pathname, path) => pathname === path;
const isGroupActive = (pathname, children) => children.some(c => pathname === c.path);

/* ── SIDEBAR ── */
export const Sidebar = memo(({location, navigate, user, onLogout})=>{
  const [open,setOpen]=useState({team:true,leads:false,campaigns:false});
  const toggle=useCallback(k=>setOpen(o=>({...o,[k]:!o[k]})),[]);
  const go=useCallback(path=>navigate(path),[navigate]);
  const pathname = location.pathname;

  const renderItem = (item) => {
    if (item.ch) {
      const anyAct = isGroupActive(pathname, item.ch);
      return (
        <div key={item.id} className={`ng${open[item.id]?" open":""}`}>
          <div className={`ni${anyAct?" act":""}`} onClick={()=>toggle(item.id)}>
            <span className="ni-ico">{item.ico}</span>
            <span style={{flex:1}}>{item.lbl}</span>
            <span className="ni-arr">›</span>
          </div>
          <div className="nc-wrap">
            {item.ch.map(c=>(
              <div key={c.path} className={`nc${isActive(pathname,c.path)?" act":""}`} onClick={()=>go(c.path)}>{c.lbl}</div>
            ))}
          </div>
        </div>
      );
    }
    return (
      <div key={item.path} className={`ni${isActive(pathname,item.path)?" act":""}`} onClick={()=>go(item.path)}>
        <span className="ni-ico">{item.ico}</span>
        <span style={{flex:1}}>{item.lbl}</span>
        {item.badge==="new"&&<span className="ni-new">NEW</span>}
        {typeof item.badge==="number"&&<span className="ni-badge">{item.badge}</span>}
      </div>
    );
  };

  return (
    <aside className="sb">
      <div className="sb-top">
        <div className="sb-brand">
          <img src={process.env.PUBLIC_URL + '/logo.png'} alt="GOtaps" style={{height:28,objectFit:'contain'}}/>
          <span className="sb-tag">Enterprise</span>
        </div>
        <div className="sb-plan"><div className="sb-plan-dot"/>Enterprise Plan · Active</div>
      </div>
      <nav className="sb-nav">
        <div className="nav-grp-label">Main</div>
        {NAV.slice(0,2).map(renderItem)}
        <div className="divx"/>
        <div className="nav-grp-label">Team</div>
        {NAV.slice(2,4).map(renderItem)}
        <div className="divx"/>
        <div className="nav-grp-label">Growth</div>
        {NAV.slice(4,7).map(renderItem)}
        <div className="divx"/>
        <div className="nav-grp-label">Platform</div>
        {NAV.slice(7).map(renderItem)}
      </nav>
      <div className="sb-foot">
        <div className="uc" style={{flexDirection:"column",alignItems:"stretch",gap:0,padding:"6px 8px",cursor:"default"}}>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            <Av name={user?(user.name||"U").slice(0,2).toUpperCase():"AK"} grad="linear-gradient(150deg,#64748b,#059669)" size={30} online/>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:12.5,fontWeight:570,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{user?.name||"Alex Koder"}</div>
              <div style={{fontSize:10.5,color:"var(--t3)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{user?.email||"Enterprise Admin"}</div>
            </div>
          </div>
          {onLogout&&<button onClick={onLogout} style={{marginTop:8,width:"100%",padding:"6px 0",background:"rgba(220,38,38,.07)",border:"1px solid rgba(220,38,38,.14)",borderRadius:8,color:"var(--red)",fontSize:11.5,fontWeight:590,cursor:"pointer",transition:"all .15s",fontFamily:"var(--f)"}} onMouseEnter={e=>e.currentTarget.style.background="rgba(220,38,38,.12)"} onMouseLeave={e=>e.currentTarget.style.background="rgba(220,38,38,.07)"}>Sign Out</button>}
        </div>
      </div>
    </aside>
  );
});

/* ══════════════════════════════════════════════════
   NOTIFICATION DRAWER
══════════════════════════════════════════════════ */
export const TYPE_COLORS = {
  ai:      "var(--purple)",
  assign:  "var(--blue)",
  campaign:"var(--cyan)",
  trigger: "var(--amber)",
  billing: "var(--green)",
  team:    "var(--green)",
  admin:   "var(--red)",
  view:    "var(--cyan)",
  security:"var(--red)",
  approval:"var(--blue)",
};
export const TYPE_ICONS = {
  ai:"🤖", assign:"🎯", campaign:"📣", trigger:"⚡", billing:"💳",
  team:"👥", admin:"📢", view:"👁", security:"🔒", approval:"✅",
};
export const TYPE_LABELS = {
  ai:"Ai", assign:"Assign", campaign:"Campaign", trigger:"Trigger", billing:"Billing",
  team:"Team", admin:"Admin", view:"View", security:"Security", approval:"Approval",
};

function relativeTime(ts) {
  if (!ts) return "";
  const ms = ts.toMillis ? ts.toMillis() : (ts._seconds ? ts._seconds*1000 : (typeof ts === 'number' ? ts : Date.parse(ts)));
  if (!ms) return "";
  const d = Date.now() - ms;
  if (d < 60000) return "now";
  if (d < 3600000) return `${Math.floor(d/60000)}m ago`;
  if (d < 86400000) return `${Math.floor(d/3600000)}h ago`;
  return `${Math.floor(d/86400000)}d ago`;
}

export const NotifRow = memo(({row, userId, navigate, onClose}) => {
  const cat = row.category || 'team';
  const color = TYPE_COLORS[cat] || "var(--blue)";
  const icon  = TYPE_ICONS[cat]  || "🔔";
  const label = TYPE_LABELS[cat] || cat;
  const unread = !row.readAt;

  const handleClick = async () => {
    if (unread) await markAsRead(userId, row).catch(()=>{});
    if (row.action) navigate(row.action);
    if (onClose) onClose();
  };
  const handleDelete = async (e) => {
    e.stopPropagation();
    await deleteNotification(userId, row).catch(()=>{});
  };

  return (
    <div onClick={handleClick}
      style={{position:"relative",display:"flex",gap:12,padding:"12px 14px",borderRadius:14,
              background: unread ? "rgba(59,130,246,.06)" : "rgba(255,255,255,.6)",
              border:"1px solid rgba(0,0,0,.05)",cursor:"pointer",transition:"background .15s"}}>
      {unread && <div style={{position:"absolute",left:6,top:18,width:6,height:6,borderRadius:"50%",background:"var(--blue)"}}/>}
      <div style={{width:40,height:40,borderRadius:12,background:`color-mix(in srgb, ${color} 14%, transparent)`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,flexShrink:0,marginLeft:4}}>{icon}</div>
      <div style={{flex:1,minWidth:0}}>
        <div style={{display:"flex",alignItems:"baseline",justifyContent:"space-between",gap:8}}>
          <div style={{fontSize:13.5,fontWeight:700,color:"var(--t1)",lineHeight:1.3}}>{row.title || "Notification"}</div>
          <div style={{fontSize:11,color:"var(--t4)",whiteSpace:"nowrap"}}>{relativeTime(row.createdAt)}</div>
        </div>
        <div style={{fontSize:12.5,color:"var(--t3)",lineHeight:1.5,marginTop:3,marginBottom:8,wordBreak:"break-word"}}>
          {row.body}{row.count > 1 ? ` (${row.count})` : ""}
        </div>
        <span style={{display:"inline-block",padding:"3px 10px",fontSize:11,fontWeight:600,color:color,
                     background:`color-mix(in srgb, ${color} 12%, transparent)`,borderRadius:999}}>{label}</span>
      </div>
      {!row.isBroadcast && (
        <button onClick={handleDelete} title="Dismiss"
          style={{alignSelf:"flex-start",width:22,height:22,border:"none",background:"transparent",color:"var(--t4)",cursor:"pointer",fontSize:14,padding:0}}>✕</button>
      )}
    </div>
  );
});

const NotifDrawer = memo(({open, onClose, navigate}) => {
  const { user, inbox, unreadCount } = useApp();
  const [tab, setTab] = useState("all"); // all | unread

  useEffect(() => {
    if (!open) return;
    const onKey = e => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const visible = useMemo(
    () => tab === "unread" ? inbox.filter(r => !r.readAt) : inbox,
    [inbox, tab]
  );
  const canCompose = user?.role === 'enterpriser' || user?.role === 'admin';

  if (!open) return null;

  return (
    <>
      <style>{`
        @keyframes notifSlideIn { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
        @keyframes notifFadeIn { from { opacity: 0; } to { opacity: 1; } }
      `}</style>
      <div onClick={onClose} style={{position:"fixed",inset:0,background:"rgba(0,0,0,.3)",backdropFilter:"blur(4px)",WebkitBackdropFilter:"blur(4px)",zIndex:9998,animation:"notifFadeIn .2s ease"}} />
      <div style={{position:"fixed",top:0,right:0,bottom:0,width:420,maxWidth:"100vw",background:"rgba(255,255,255,.92)",backdropFilter:"blur(24px)",WebkitBackdropFilter:"blur(24px)",borderLeft:"1px solid rgba(0,0,0,.08)",zIndex:9999,display:"flex",flexDirection:"column",animation:"notifSlideIn .3s cubic-bezier(.16,1,.3,1)"}}>
        {/* Header */}
        <div style={{padding:"18px 20px 12px",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <div style={{fontSize:20,fontWeight:700,fontFamily:"var(--fd)"}}>Notifications</div>
            {unreadCount > 0 && (
              <div style={{minWidth:26,height:22,padding:"0 8px",borderRadius:999,background:"var(--red)",color:"#fff",fontSize:12,fontWeight:700,display:"flex",alignItems:"center",justifyContent:"center"}}>{unreadCount}</div>
            )}
          </div>
          <div style={{display:"flex",gap:10,alignItems:"center"}}>
            {unreadCount > 0 && (
              <button onClick={()=>markAllRead(user?.id, inbox).catch(()=>{})}
                style={{border:"none",background:"transparent",color:"var(--blue)",fontSize:12.5,fontWeight:600,cursor:"pointer"}}>Mark all read</button>
            )}
            <button onClick={onClose} style={{width:30,height:30,display:"flex",alignItems:"center",justifyContent:"center",border:"1px solid rgba(0,0,0,.08)",borderRadius:8,background:"rgba(0,0,0,.03)",cursor:"pointer",fontSize:14,color:"var(--t3)"}}>✕</button>
          </div>
        </div>

        {/* Tabs */}
        <div style={{padding:"0 20px 10px",display:"flex",gap:8,alignItems:"center",justifyContent:"space-between"}}>
          <div style={{display:"flex",gap:8}}>
            <button onClick={()=>setTab("all")}
              style={{padding:"6px 14px",fontSize:12.5,fontWeight:600,borderRadius:999,border:"1px solid rgba(0,0,0,.08)",
                      background: tab==="all" ? "var(--t1)" : "transparent",
                      color: tab==="all" ? "#fff" : "var(--t2)",cursor:"pointer"}}>All</button>
            <button onClick={()=>setTab("unread")}
              style={{padding:"6px 14px",fontSize:12.5,fontWeight:600,borderRadius:999,border:"1px solid rgba(0,0,0,.08)",
                      background: tab==="unread" ? "var(--t1)" : "transparent",
                      color: tab==="unread" ? "#fff" : "var(--t2)",cursor:"pointer"}}>Unread ({unreadCount})</button>
          </div>
          <button onClick={()=>{onClose(); navigate('/notifications');}}
            style={{border:"none",background:"transparent",color:"var(--t3)",fontSize:12,cursor:"pointer"}}>⚙ Settings</button>
        </div>

        {/* List */}
        <div style={{flex:1,overflowY:"auto",padding:"6px 16px 90px",display:"flex",flexDirection:"column",gap:10}}>
          {visible.length === 0 && (
            <div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"60px 20px",textAlign:"center"}}>
              <div style={{width:60,height:60,borderRadius:18,background:"rgba(100,116,139,.08)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:26,marginBottom:14}}>🔔</div>
              <div style={{fontSize:15,fontWeight:700,color:"var(--t1)",marginBottom:4,fontFamily:"var(--fd)"}}>You're all caught up</div>
              <div style={{fontSize:12.5,color:"var(--t3)"}}>No new notifications.</div>
            </div>
          )}
          {visible.map(row => (
            <NotifRow key={row.id} row={row} userId={user?.id} navigate={navigate} onClose={onClose}/>
          ))}
        </div>

        {/* Footer */}
        <div style={{position:"absolute",left:16,right:16,bottom:16,display:"flex",alignItems:"center",gap:10}}>
          <button onClick={()=>{onClose(); navigate('/notifications');}}
            style={{flex:1,padding:"12px",border:"1px solid rgba(0,0,0,.08)",borderRadius:14,background:"rgba(255,255,255,.8)",backdropFilter:"blur(12px)",cursor:"pointer",fontSize:13,fontWeight:600,color:"var(--t1)"}}>View All Notifications →</button>
          {canCompose && (
            <button onClick={()=>{onClose(); navigate('/campaigns/notification');}}
              title="Compose notification"
              style={{width:48,height:48,borderRadius:"50%",border:"none",background:"var(--t1)",color:"#fff",fontSize:22,cursor:"pointer",boxShadow:"0 6px 20px rgba(0,0,0,.2)"}}>+</button>
          )}
        </div>
      </div>
    </>
  );
});

/* ══════════════════════════════════════════════════
   SHORTCUTS PANEL
══════════════════════════════════════════════════ */
const SHORTCUT_GROUPS = [
  {label:"Navigation",items:[
    {desc:"Go to Dashboard",keys:["G","D"]},
    {desc:"Go to Leads",keys:["G","L"]},
    {desc:"Go to Team",keys:["G","T"]},
    {desc:"Go to Settings",keys:["G","S"]},
    {desc:"Search",keys:["Cmd","K"]},
  ]},
  {label:"Actions",items:[
    {desc:"Create Lead",keys:["C","L"]},
    {desc:"Create User",keys:["C","U"]},
    {desc:"New Campaign",keys:["C","M"]},
    {desc:"Ask AI",keys:["Cmd","J"]},
  ]},
  {label:"Interface",items:[
    {desc:"Toggle Sidebar",keys:["Cmd","B"]},
    {desc:"Toggle Dark Mode",keys:["Cmd","D"]},
    {desc:"Close Modal / Panel",keys:["Esc"]},
    {desc:"Show Shortcuts",keys:["?"]},
  ]},
];

const ShortcutsPanel = memo(({open, onClose}) => {
  useEffect(() => {
    if (!open) return;
    const onKey = e => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      <style>{`
        @keyframes scaleIn { from { transform: scale(.95); opacity: 0; } to { transform: scale(1); opacity: 1; } }
      `}</style>
      <div onClick={onClose} style={{position:"fixed",inset:0,background:"rgba(0,0,0,.35)",backdropFilter:"blur(6px)",WebkitBackdropFilter:"blur(6px)",zIndex:9998,display:"flex",alignItems:"center",justifyContent:"center"}}>
        <div onClick={e => e.stopPropagation()} style={{width:480,maxWidth:"90vw",maxHeight:"80vh",overflowY:"auto",background:"rgba(255,255,255,.96)",backdropFilter:"blur(24px)",WebkitBackdropFilter:"blur(24px)",borderRadius:16,border:"1px solid rgba(0,0,0,.08)",boxShadow:"0 24px 80px rgba(0,0,0,.18)",animation:"scaleIn .2s cubic-bezier(.16,1,.3,1)",padding:"24px"}}>
          {/* Header */}
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:20}}>
            <div style={{fontSize:16,fontWeight:700,fontFamily:"var(--f)"}}>Keyboard Shortcuts</div>
            <button onClick={onClose} style={{width:28,height:28,borderRadius:7,border:"1px solid rgba(0,0,0,.08)",background:"rgba(0,0,0,.03)",cursor:"pointer",fontSize:13,color:"var(--t3)",display:"flex",alignItems:"center",justifyContent:"center"}}>✕</button>
          </div>

          {/* Groups */}
          {SHORTCUT_GROUPS.map(g => (
            <div key={g.label} style={{marginBottom:18}}>
              <div style={{fontSize:10.5,fontWeight:700,textTransform:"uppercase",letterSpacing:".06em",color:"var(--t4)",marginBottom:8}}>{g.label}</div>
              {g.items.map((s,i) => (
                <div key={i} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"7px 0",borderBottom:i<g.items.length-1?"1px solid rgba(0,0,0,.04)":"none"}}>
                  <span style={{fontSize:12.5,color:"var(--t2)"}}>{s.desc}</span>
                  <div style={{display:"flex",gap:4}}>
                    {s.keys.map((k,j) => (
                      <kbd key={j} style={{padding:"2px 7px",fontSize:10.5,fontWeight:600,fontFamily:"var(--f)",background:"rgba(0,0,0,.05)",border:"1px solid rgba(0,0,0,.1)",borderRadius:5,color:"var(--t2)",minWidth:20,textAlign:"center"}}>{k}</kbd>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ))}

          {/* Footer */}
          <div style={{textAlign:"center",paddingTop:8,borderTop:"1px solid rgba(0,0,0,.06)"}}>
            <span style={{fontSize:11,color:"var(--t4)"}}>Press </span>
            <kbd style={{padding:"2px 7px",fontSize:10,fontWeight:600,fontFamily:"var(--f)",background:"rgba(0,0,0,.05)",border:"1px solid rgba(0,0,0,.1)",borderRadius:5,color:"var(--t3)"}}>?</kbd>
            <span style={{fontSize:11,color:"var(--t4)"}}> anytime to view shortcuts</span>
          </div>
        </div>
      </div>
    </>
  );
});

/* ══════════════════════════════════════════════════
   PROFILE POPOVER
══════════════════════════════════════════════════ */
const ProfilePopover = memo(({open, onClose, navigate, user, onLogout}) => {
  const ref = useRef(null);
  const [counts, setCounts] = useState({members:null, leads:null, subteams:null});

  useEffect(() => {
    if (!open) return;
    const onClick = e => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    getDashboardStats().then(stats => {
      if (!alive) return;
      setCounts({
        members:  stats?.totalMembers   ?? null,
        leads:    stats?.totalLeads     ?? null,
        subteams: stats?.totalSubteams  ?? null,
      });
    }).catch(() => {});
    return () => { alive = false; };
  }, [open]);

  if (!open) return null;

  const name = user?.name || "Alex Koder";
  const initials = user ? (user.name || "U").slice(0, 2).toUpperCase() : "AK";
  const role = user?.email || "Enterprise Admin";

  const menuItems = [
    {ico:"👤",label:"Edit Profile",path:"/settings"},
    {ico:"🎯",label:"My Leads",path:"/leads"},
    {ico:"📈",label:"My Analytics",path:"/leads/insights"},
    {ico:"⚙️",label:"Settings",path:"/settings"},
    {ico:"🔔",label:"Notifications",path:"/notifications"},
  ];

  return (
    <>
      <style>{`
        @keyframes popIn { from { transform: translateY(-8px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
      `}</style>
      <div ref={ref} style={{position:"absolute",top:"100%",right:0,marginTop:8,width:280,background:"rgba(255,255,255,.97)",backdropFilter:"blur(24px)",WebkitBackdropFilter:"blur(24px)",borderRadius:14,border:"1px solid rgba(0,0,0,.08)",boxShadow:"0 16px 56px rgba(0,0,0,.14)",zIndex:9999,animation:"popIn .2s cubic-bezier(.16,1,.3,1)",overflow:"hidden"}}>
        {/* User info */}
        <div style={{padding:"16px 16px 12px",borderBottom:"1px solid rgba(0,0,0,.06)"}}>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <Av name={initials} grad="linear-gradient(150deg,#64748b,#059669)" size={38} online/>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:13.5,fontWeight:660,color:"var(--t1)"}}>{name}</div>
              <div style={{fontSize:11,color:"var(--t3)",display:"flex",alignItems:"center",gap:4}}>
                <div style={{width:6,height:6,borderRadius:3,background:"#22c55e"}} />
                {role}
              </div>
            </div>
          </div>
        </div>

        {/* Account stats row */}
        <div style={{display:"flex",padding:"10px 16px",borderBottom:"1px solid rgba(0,0,0,.06)",gap:0}}>
          {[
            {label:"Members",  value:counts.members},
            {label:"Leads",    value:counts.leads},
            {label:"Subteams", value:counts.subteams},
          ].map((s,i) => (
            <div key={i} style={{flex:1,textAlign:"center",borderRight:i<2?"1px solid rgba(0,0,0,.06)":"none"}}>
              <div style={{fontSize:14,fontWeight:720,color:"var(--t1)"}}>{s.value ?? "–"}</div>
              <div style={{fontSize:10,color:"var(--t4)",fontWeight:550}}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Menu */}
        <div style={{padding:"6px 8px"}}>
          {menuItems.map((m,i) => (
            <div key={i} onClick={() => { onClose(); navigate(m.path); }} style={{padding:"8px 10px",borderRadius:8,cursor:"pointer",display:"flex",alignItems:"center",gap:9,fontSize:12.5,fontWeight:540,color:"var(--t2)",transition:"all .12s"}} onMouseEnter={e => e.currentTarget.style.background="rgba(0,0,0,.04)"} onMouseLeave={e => e.currentTarget.style.background="transparent"}>
              <span style={{fontSize:14}}>{m.ico}</span>
              {m.label}
            </div>
          ))}
        </div>

        {/* Sign out */}
        {onLogout && (
          <div style={{padding:"6px 8px",borderTop:"1px solid rgba(0,0,0,.06)"}}>
            <div onClick={() => { onClose(); onLogout(); }} style={{padding:"8px 10px",borderRadius:8,cursor:"pointer",display:"flex",alignItems:"center",gap:9,fontSize:12.5,fontWeight:600,color:"var(--red)",transition:"all .12s"}} onMouseEnter={e => e.currentTarget.style.background="rgba(220,38,38,.06)"} onMouseLeave={e => e.currentTarget.style.background="transparent"}>
              <span style={{fontSize:14}}>🚪</span>
              Sign Out
            </div>
          </div>
        )}
      </div>
    </>
  );
});

/* ── TOPBAR ── */
export const Topbar = memo(({location, navigate, pathLabels, user, onLogout})=>{
  const [notifOpen, setNotifOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const { unreadCount } = useApp();

  const segments = location.pathname.split('/').filter(Boolean);
  const label = pathLabels[segments[segments.length-1]] || pathLabels[segments[0]] || 'Dashboard';

  return (
    <>
      <header className="tb">
        <div className="tb-bc">
          <span>GOtaps</span>
          <span style={{color:"var(--t4)"}}>›</span>
          <b>{label}</b>
        </div>
        <div className="tb-search"><span style={{fontSize:13}}>🔍</span><span className="tb-s-txt">Search anything…</span><span className="tb-kbd">⌘K</span></div>
        <div className="ai-btn" onClick={()=>navigate("/ai")}><div className="ai-pulse"/><span>Ask AI</span></div>
        <div className="tb-acts">
          <div className="tb-btn" onClick={()=>setNotifOpen(true)} style={{position:"relative"}}>
            🔔
            {unreadCount > 0 && (
              <div style={{position:"absolute",top:-2,right:-2,minWidth:16,height:16,padding:"0 4px",borderRadius:999,background:"var(--red)",color:"#fff",fontSize:10,fontWeight:700,display:"flex",alignItems:"center",justifyContent:"center",border:"2px solid #fff"}}>
                {unreadCount > 99 ? "99+" : unreadCount}
              </div>
            )}
          </div>
          <div className="tb-btn">💬</div>
          <div className="tb-btn" onClick={()=>setShortcutsOpen(true)} title="Keyboard shortcuts">?</div>
          <div style={{position:"relative"}}>
            <div onClick={()=>setProfileOpen(p=>!p)} style={{cursor:"pointer"}}>
              <Av name={user?(user.name||"U").slice(0,2).toUpperCase():"AK"} grad="linear-gradient(150deg,#64748b,#059669)" size={32} online/>
            </div>
            <ProfilePopover open={profileOpen} onClose={()=>setProfileOpen(false)} navigate={navigate} user={user} onLogout={onLogout} />
          </div>
        </div>
      </header>

      <NotifDrawer open={notifOpen} onClose={()=>setNotifOpen(false)} navigate={navigate} />
      <ShortcutsPanel open={shortcutsOpen} onClose={()=>setShortcutsOpen(false)} />
    </>
  );
});
